import { Box3, BufferAttribute, BufferGeometry, Matrix4, Vector3 } from 'three'
import { GLB_INSTANCE_OWNED_GEOMETRY } from './disposeGlbClone'

const CAD_UV_SPAN = 2
const box = new Box3()
const size = new Vector3()
const worldDirA = new Vector3()
const worldDirB = new Vector3()
const worldUp = new Vector3(0, 1, 0)

type Axis = 'x' | 'y' | 'z'
type CatalogSwatchUvMode = 'panel' | 'face' | 'lface'

function uvSpanIsCadRange(geometry: BufferGeometry): boolean {
  if (geometry.userData.__catalogSwatchUvs) return false
  const uv = geometry.attributes.uv
  if (!uv || uv.count === 0) return true
  let minU = Infinity
  let minV = Infinity
  let maxU = -Infinity
  let maxV = -Infinity
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i)
    const v = uv.getY(i)
    if (u < minU) minU = u
    if (v < minV) minV = v
    if (u > maxU) maxU = u
    if (v > maxV) maxV = v
  }
  return maxU - minU > CAD_UV_SPAN || maxV - minV > CAD_UV_SPAN
}

function wantedUvMode(
  panel: boolean | undefined,
  isRinconero?: boolean,
): CatalogSwatchUvMode {
  if (!panel) return 'face'
  // Solo el rinconero: una malla Puerta en L (p.ej. KBRB80932). El resto sigue panel AABB.
  if (isRinconero) return 'lface'
  return 'panel'
}

function axisCoord(axis: Axis, x: number, y: number, z: number): number {
  if (axis === 'x') return x
  if (axis === 'y') return y
  return z
}

function localAxisWorldDir(
  matrixWorld: Matrix4 | undefined,
  axis: Axis,
  target: Vector3,
): Vector3 {
  if (!matrixWorld) {
    if (axis === 'x') return target.set(1, 0, 0)
    if (axis === 'y') return target.set(0, 1, 0)
    return target.set(0, 0, 1)
  }
  const e = matrixWorld.elements
  if (axis === 'x') return target.set(e[0], e[1], e[2]).normalize()
  if (axis === 'y') return target.set(e[4], e[5], e[6]).normalize()
  return target.set(e[8], e[9], e[10]).normalize()
}

/**
 * three@0.160 BufferGeometry.copy() assigns `userData` by reference.
 * Flags written on a clone leak to the catalog cache and every other instance.
 */
export function isolateGeometryUserData(geometry: BufferGeometry): BufferGeometry {
  geometry.userData = { ...geometry.userData }
  return geometry
}

function writeFaceNormalUvs(
  owned: BufferGeometry,
  sx: number,
  sy: number,
  sz: number,
): void {
  const pos = owned.attributes.position
  const nrm = owned.attributes.normal
  const uvs = new Float32Array(pos.count * 2)

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    const nx = Math.abs(nrm?.getX(i) ?? 0)
    const ny = Math.abs(nrm?.getY(i) ?? 0)
    const nz = Math.abs(nrm?.getZ(i) ?? 0)
    let u = 0
    let v = 0
    if (nx >= ny && nx >= nz) {
      u = (z - box.min.z) / sz
      v = (y - box.min.y) / sy
    } else if (ny >= nx && ny >= nz) {
      u = (x - box.min.x) / sx
      v = (z - box.min.z) / sz
    } else {
      u = (x - box.min.x) / sx
      v = (y - box.min.y) / sy
    }
    uvs[i * 2] = u
    uvs[i * 2 + 1] = v
  }

  owned.setAttribute('uv', new BufferAttribute(uvs, 2))
}

function writePanelUvs(
  mesh: {
    matrixWorld?: Matrix4
    updateWorldMatrix?: (updateParents: boolean, updateChildren: boolean) => void
  },
  owned: BufferGeometry,
  sx: number,
  sy: number,
  sz: number,
): void {
  const axes: { axis: Axis; size: number; min: number }[] = [
    { axis: 'x', size: sx, min: box.min.x },
    { axis: 'y', size: sy, min: box.min.y },
    { axis: 'z', size: sz, min: box.min.z },
  ].sort((a, b) => b.size - a.size)
  const faceA = axes[0]
  const faceB = axes[1]
  const yFace = axes.find((entry) => entry.axis === 'y' && (entry === faceA || entry === faceB))
  let vAxis = yFace ?? faceA
  let uAxis = vAxis === faceA ? faceB : faceA
  if (!yFace) {
    mesh.updateWorldMatrix?.(true, false)
    localAxisWorldDir(mesh.matrixWorld, faceA.axis, worldDirA)
    localAxisWorldDir(mesh.matrixWorld, faceB.axis, worldDirB)
    vAxis =
      Math.abs(worldDirA.dot(worldUp)) >= Math.abs(worldDirB.dot(worldUp)) ? faceA : faceB
    uAxis = vAxis === faceA ? faceB : faceA
  }

  const pos = owned.attributes.position
  const uvs = new Float32Array(pos.count * 2)
  const uSize = uAxis.size || 1
  const vSize = vAxis.size || 1

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    uvs[i * 2] = (axisCoord(uAxis.axis, x, y, z) - uAxis.min) / uSize
    uvs[i * 2 + 1] = (axisCoord(vAxis.axis, x, y, z) - vAxis.min) / vSize
  }

  owned.setAttribute('uv', new BufferAttribute(uvs, 2))
}

const triA = new Vector3()
const triB = new Vector3()
const triC = new Vector3()
const triAB = new Vector3()
const triAC = new Vector3()
const triN = new Vector3()

function dominantAxis(nx: number, ny: number, nz: number): Axis {
  const ax = Math.abs(nx)
  const ay = Math.abs(ny)
  const az = Math.abs(nz)
  if (ax >= ay && ax >= az) return 'x'
  if (ay >= ax && ay >= az) return 'y'
  return 'z'
}

function groupedUvCoord(axis: Axis, x: number, y: number, z: number): { u: number; v: number } {
  if (axis === 'x') return { u: z, v: y }
  if (axis === 'y') return { u: x, v: z }
  return { u: x, v: y }
}

function triangleDominantAxis(pos: { getX: (i: number) => number; getY: (i: number) => number; getZ: (i: number) => number }, i0: number): Axis {
  triA.set(pos.getX(i0), pos.getY(i0), pos.getZ(i0))
  triB.set(pos.getX(i0 + 1), pos.getY(i0 + 1), pos.getZ(i0 + 1))
  triC.set(pos.getX(i0 + 2), pos.getY(i0 + 2), pos.getZ(i0 + 2))
  triAB.subVectors(triB, triA)
  triAC.subVectors(triC, triA)
  triN.crossVectors(triAB, triAC)
  return dominantAxis(triN.x, triN.y, triN.z)
}

/**
 * Rinconero en L: unwrap 0–1 por cara (normal de triángulo, geometría no indexada).
 * writePanelUvs sobre el AABB usa XZ (planta) y corta el swatch.
 * Las normales de vértice suavizadas no sirven: el UV compartido pixela el grano.
 */
function writePerTrianglePanelUvs(
  mesh: { geometry?: BufferGeometry },
  owned: BufferGeometry,
): BufferGeometry {
  let geom = owned
  if (owned.index) {
    const next = owned.toNonIndexed()
    isolateGeometryUserData(next)
    next.userData = { ...owned.userData }
    next.userData[GLB_INSTANCE_OWNED_GEOMETRY] = true
    mesh.geometry = next
    owned.dispose()
    geom = next
  }

  const pos = geom.attributes.position
  const triCount = Math.floor(pos.count / 3)
  const extents: Record<Axis, { uMin: number; uMax: number; vMin: number; vMax: number }> = {
    x: { uMin: Infinity, uMax: -Infinity, vMin: Infinity, vMax: -Infinity },
    y: { uMin: Infinity, uMax: -Infinity, vMin: Infinity, vMax: -Infinity },
    z: { uMin: Infinity, uMax: -Infinity, vMin: Infinity, vMax: -Infinity },
  }

  for (let t = 0; t < triCount; t++) {
    const i0 = t * 3
    const axis = triangleDominantAxis(pos, i0)
    const e = extents[axis]
    for (let k = 0; k < 3; k++) {
      const i = i0 + k
      const { u, v } = groupedUvCoord(axis, pos.getX(i), pos.getY(i), pos.getZ(i))
      if (u < e.uMin) e.uMin = u
      if (u > e.uMax) e.uMax = u
      if (v < e.vMin) e.vMin = v
      if (v > e.vMax) e.vMax = v
    }
  }

  const uvs = new Float32Array(pos.count * 2)
  for (let t = 0; t < triCount; t++) {
    const i0 = t * 3
    const axis = triangleDominantAxis(pos, i0)
    const e = extents[axis]
    const uSize = e.uMax - e.uMin || 1
    const vSize = e.vMax - e.vMin || 1
    for (let k = 0; k < 3; k++) {
      const i = i0 + k
      const { u, v } = groupedUvCoord(axis, pos.getX(i), pos.getY(i), pos.getZ(i))
      uvs[i * 2] = (u - e.uMin) / uSize
      uvs[i * 2 + 1] = (v - e.vMin) / vSize
    }
  }

  geom.setAttribute('uv', new BufferAttribute(uvs, 2))
  return geom
}

const CATALOG_SWATCH_UV_REV = 4

/** CAD-range unwrap still clamps. Furniture 0–1 panel/lface tiles with RepeatWrapping (desktop). */
export function catalogSwatchUsesClampWrap(geometry?: BufferGeometry | null): boolean {
  return geometry?.userData?.__catalogSwatchUvMode === 'face'
}

/**
 * CAD/STEP handles (e.g. 55532012835) keep logo UVs in inches (−8..19).
 * Repeating a catalog swatch on that range looks like a gold thread.
 * Furniture GLBs stay on authored 0..1 UVs unless `force` (2-door kits share one
 * atlas: each leaf is 0–0.5 / 0.5–1 and the swatch looks cut down the middle).
 * Door/costado panels with CAD-range UVs project on the two large axes (V up)
 * so the thin edge does not steal the unwrap.
 * Rinconero L-meshes: per-triangle unwrap, not AABB panel (XZ plan).
 */
export function applyCatalogSwatchUvsIfNeeded(
  mesh: {
    geometry?: BufferGeometry
    matrixWorld?: Matrix4
    updateWorldMatrix?: (updateParents: boolean, updateChildren: boolean) => void
  },
  options?: { panel?: boolean; force?: boolean; isRinconero?: boolean },
): boolean {
  const geometry = mesh.geometry
  if (!geometry?.attributes?.position) return false

  geometry.computeBoundingBox()
  if (!geometry.boundingBox) return false
  const mode = wantedUvMode(options?.panel, options?.isRinconero)
  const currentMode = geometry.userData.__catalogSwatchUvMode as CatalogSwatchUvMode | undefined
  const currentRev = geometry.userData.__catalogSwatchUvRev
  const needsCad = uvSpanIsCadRange(geometry)
  const alreadyOk =
    Boolean(geometry.userData.__catalogSwatchUvs) &&
    currentMode === mode &&
    currentRev === CATALOG_SWATCH_UV_REV &&
    !needsCad

  if (alreadyOk) return true
  if (currentMode === 'lface' && currentRev === CATALOG_SWATCH_UV_REV && !needsCad && mode !== 'lface') {
    return true
  }
  if (!options?.force && !needsCad && !geometry.userData.__catalogSwatchUvs) {
    return false
  }

  if (
    !geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] ||
    geometry.userData.__catalogSwatchUvs
  ) {
    mesh.geometry = isolateGeometryUserData(geometry.clone())
    mesh.geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] = true
  } else {
    isolateGeometryUserData(geometry)
  }
  let owned = mesh.geometry
  owned.computeBoundingBox()
  if (!owned.boundingBox) return false
  box.copy(owned.boundingBox)
  box.getSize(size)

  const ox = size.x || 1
  const oy = size.y || 1
  const oz = size.z || 1
  const appliedMode = wantedUvMode(options?.panel, options?.isRinconero)

  if (appliedMode === 'lface') {
    owned = writePerTrianglePanelUvs(mesh, owned)
  } else if (appliedMode === 'panel') {
    writePanelUvs(mesh, owned, ox, oy, oz)
  } else {
    writeFaceNormalUvs(owned, ox, oy, oz)
  }

  owned.userData.__catalogSwatchUvs = true
  owned.userData.__catalogSwatchUvMode = appliedMode
  owned.userData.__catalogSwatchUvRev = CATALOG_SWATCH_UV_REV
  return true
}

export function zoneNamesMatch(a?: string, b?: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  const normalize = (value: string) => value.replace(/[\s._<>]/g, '').toLowerCase()
  return normalize(a) === normalize(b)
}
